// Commands (PRD section 17, "Órdenes"): every player action is a command. A command is checked
// against the world and turned into a patch, a list of layout changes with the values they replace;
// applying a patch returns its inverse, which is what undo applies. Checking without applying is what
// the ghost of the blueprint mode (H2) will use to show whether something fits.
import { footprint, inBounds, isRotation, keyOf, rotatedSize, rotateSide, sideTowards, type Cell, type Rotation, type Side } from './geometry';
import { cell, fail, list, oneOf, record, side, text } from './validate';
import type { MachineDef } from './data';
import { occupancy, roadLength, type Interior, type MachineState, type RouteState, type TileState, type World } from './world';

export type Command =
  | { readonly type: 'placeMachine'; readonly tile: string; readonly machine: string; readonly cell: Cell; readonly rotation: Rotation }
  /** Belt along `path`: each cell points at the next, and the last one leaves through `exit` (by default, straight on). */
  | { readonly type: 'placeBelt'; readonly tile: string; readonly path: readonly Cell[]; readonly exit?: Side }
  /** Removes the machine or belt on `cell`. */
  | { readonly type: 'remove'; readonly tile: string; readonly cell: Cell }
  /** Turns the machine or belt on `cell` a quarter clockwise. */
  | { readonly type: 'rotate'; readonly tile: string; readonly cell: Cell }
  /** Picks up the machine covering `cell` and puts it down with its top-left corner at `to`, turned to `rotation`. */
  | { readonly type: 'moveMachine'; readonly tile: string; readonly cell: Cell; readonly to: Cell; readonly rotation: Rotation }
  /** Route from an exit of `from` to an entrance of `to`; by default, their first exit and entrance. */
  | { readonly type: 'createRoute'; readonly from: string; readonly to: string; readonly vehicle: string; readonly fromPort?: string; readonly toPort?: string }
  | { readonly type: 'removeRoute'; readonly route: string };

export type CommandType = Command['type'];

export interface Patch {
  readonly machines: readonly { readonly tile: string; readonly id: string; readonly value: MachineState | null }[];
  readonly belts: readonly { readonly tile: string; readonly key: string; readonly value: Side | null }[];
  readonly routes: readonly { readonly id: string; readonly value: RouteState | null }[];
}

export type Plan = { readonly ok: true; readonly label: string; readonly patch: Patch; readonly ids: number } | { readonly ok: false; readonly reason: string };

const EMPTY: Patch = { machines: [], belts: [], routes: [] };
const patch = (p: Partial<Patch>): Patch => ({ ...EMPTY, ...p });
const refuse = (reason: string): Plan => ({ ok: false, reason });

const lower = (name: string): string => name.charAt(0).toLowerCase() + name.slice(1);

function interiorOf(world: World, tileId: string): { tile: TileState; interior: Interior } | string {
  const tile = world.tiles.get(tileId);
  if (!tile) return `No existe la casilla «${tileId}».`;
  if (!tile.interior) return `${tile.def.name} no tiene interior donde construir.`;
  return { tile, interior: tile.interior };
}

/** Why a machine cannot go at `origin` turned to `rotation`, ignoring `itself` when it is being moved; nothing if it fits. */
function fitMachine(interior: Interior, def: MachineDef, origin: Cell, rotation: Rotation, itself?: MachineState): string | undefined {
  const cells = footprint(origin, def.size, rotation);
  if (!cells.every((c) => inBounds(c, interior.size))) return `${def.name}: no cabe dentro de la casilla.`;
  const taken = occupancy(interior);
  if (cells.some((c) => (taken.get(keyOf(c)) ?? itself) !== itself)) return `${def.name}: hay otra máquina en ese sitio.`;
  if (cells.some((c) => interior.belts.has(keyOf(c)))) return `${def.name}: hay una cinta en ese sitio.`;
  return undefined;
}

/** Checks a command against the world and turns it into a patch, without changing anything. */
export function plan(world: World, command: Command): Plan {
  switch (command.type) {
    case 'placeMachine': {
      const found = interiorOf(world, command.tile);
      if (typeof found === 'string') return refuse(found);
      const def = world.data.machines.get(command.machine);
      if (!def) return refuse(`No existe la máquina «${command.machine}».`);
      const fits = fitMachine(found.interior, def, command.cell, command.rotation);
      if (fits) return refuse(fits);
      const id = `m${world.nextId}`;
      return { ok: true, label: `Colocar ${lower(def.name)}`, ids: 1, patch: patch({ machines: [{ tile: command.tile, id, value: { id, def, origin: command.cell, rotation: command.rotation } }] }) };
    }

    case 'moveMachine': {
      const found = interiorOf(world, command.tile);
      if (typeof found === 'string') return refuse(found);
      const { interior } = found;
      const machine = occupancy(interior).get(keyOf(command.cell));
      if (!machine) return refuse('No hay ninguna máquina que mover en esa celda.');
      const fits = fitMachine(interior, machine.def, command.to, command.rotation, machine);
      if (fits) return refuse(fits);
      if (keyOf(command.to) === keyOf(machine.origin) && command.rotation === machine.rotation) return refuse(`${machine.def.name}: ya está en ese sitio.`);
      return {
        ok: true,
        label: `Mover ${lower(machine.def.name)}`,
        ids: 0,
        patch: patch({ machines: [{ tile: command.tile, id: machine.id, value: { ...machine, origin: command.to, rotation: command.rotation } }] }),
      };
    }

    case 'placeBelt': {
      const found = interiorOf(world, command.tile);
      if (typeof found === 'string') return refuse(found);
      const { interior } = found;
      const { path } = command;
      if (path.length === 0) return refuse('La cinta no tiene celdas.');
      if (!path.every((c) => inBounds(c, interior.size))) return refuse('La cinta se sale de la casilla.');
      if (new Set(path.map(keyOf)).size !== path.length) return refuse('La cinta pasa dos veces por la misma celda.');
      const taken = occupancy(interior);
      const blocker = path.map((c) => taken.get(keyOf(c))).find((m) => m !== undefined);
      if (blocker) return refuse(`La cinta pasa por encima de ${lower(blocker.def.name)}.`);
      const sides: Side[] = [];
      for (let i = 0; i + 1 < path.length; i++) {
        const towards = sideTowards(path[i] as Cell, path[i + 1] as Cell);
        if (!towards) return refuse('Las celdas de la cinta deben ir seguidas.');
        sides.push(towards);
      }
      const exit = command.exit ?? sides[sides.length - 1];
      if (!exit) return refuse('Falta la dirección de la cinta.');
      sides.push(exit);
      return {
        ok: true,
        label: path.length === 1 ? 'Poner cinta' : `Trazar cinta de ${path.length} celdas`,
        ids: 0,
        patch: patch({ belts: path.map((c, i) => ({ tile: command.tile, key: keyOf(c), value: sides[i] ?? exit })) }),
      };
    }

    case 'remove':
    case 'rotate': {
      const found = interiorOf(world, command.tile);
      if (typeof found === 'string') return refuse(found);
      const { interior } = found;
      const key = keyOf(command.cell);
      const machine = occupancy(interior).get(key);
      const belt = interior.belts.get(key);
      if (command.type === 'remove') {
        if (machine) return { ok: true, label: `Quitar ${lower(machine.def.name)}`, ids: 0, patch: patch({ machines: [{ tile: command.tile, id: machine.id, value: null }] }) };
        if (belt) return { ok: true, label: 'Quitar cinta', ids: 0, patch: patch({ belts: [{ tile: command.tile, key, value: null }] }) };
        return refuse('No hay nada que quitar en esa celda.');
      }
      if (machine) {
        const rotation = ((machine.rotation + 1) % 4) as Rotation;
        // A square machine turns in place; any other shape must still fit once turned.
        const [w, h] = rotatedSize(machine.def.size, rotation);
        if (w !== h) {
          const cells = footprint(machine.origin, machine.def.size, rotation);
          const taken = occupancy(interior);
          const clash = cells.some((c) => !inBounds(c, interior.size) || interior.belts.has(keyOf(c)) || (taken.get(keyOf(c)) ?? machine) !== machine);
          if (clash) return refuse(`${machine.def.name}: no cabe al girarla.`);
        }
        return { ok: true, label: `Girar ${lower(machine.def.name)}`, ids: 0, patch: patch({ machines: [{ tile: command.tile, id: machine.id, value: { ...machine, rotation } }] }) };
      }
      if (belt) return { ok: true, label: 'Girar cinta', ids: 0, patch: patch({ belts: [{ tile: command.tile, key, value: rotateSide(belt, 1) }] }) };
      return refuse('No hay nada que girar en esa celda.');
    }

    case 'createRoute': {
      const from = world.tiles.get(command.from);
      const to = world.tiles.get(command.to);
      if (!from) return refuse(`No existe la casilla «${command.from}».`);
      if (!to) return refuse(`No existe la casilla «${command.to}».`);
      if (from === to) return refuse('El origen y el destino de una ruta deben ser casillas distintas.');
      const vehicle = world.data.vehicles.get(command.vehicle);
      if (!vehicle) return refuse(`No existe el vehículo «${command.vehicle}».`);
      const fromPort = from.def.ports.find((p) => p.kind === 'out' && (command.fromPort === undefined || p.id === command.fromPort));
      const toPort = to.def.ports.find((p) => p.kind === 'in' && (command.toPort === undefined || p.id === command.toPort));
      if (!fromPort) return refuse(`${from.def.name} no tiene esa salida.`);
      if (!toPort) return refuse(`${to.def.name} no tiene esa entrada.`);
      for (const r of world.routes.values()) {
        if (r.from.tile === from.def.id && r.from.port === fromPort.id && r.to.tile === to.def.id && r.to.port === toPort.id) return refuse('Ya hay una ruta entre esas puertas.');
      }
      const id = `r${world.nextId}`;
      const route: RouteState = { id, from: { tile: from.def.id, port: fromPort.id }, to: { tile: to.def.id, port: toPort.id }, vehicle, length: roadLength(from.def.cell, to.def.cell) };
      return { ok: true, label: `Crear ruta ${from.def.name} → ${to.def.name}`, ids: 1, patch: patch({ routes: [{ id, value: route }] }) };
    }

    case 'removeRoute': {
      const route = world.routes.get(command.route);
      if (!route) return refuse('Esa ruta ya no existe.');
      const name = (id: string): string => world.tiles.get(id)?.def.name ?? id;
      return { ok: true, label: `Quitar ruta ${name(route.from.tile)} → ${name(route.to.tile)}`, ids: 0, patch: patch({ routes: [{ id: route.id, value: null }] }) };
    }
  }
}

/** Applies a patch and returns the patch that undoes it. */
export function applyPatch(world: World, change: Patch): Patch {
  const inverse: { machines: Patch['machines'][number][]; belts: Patch['belts'][number][]; routes: Patch['routes'][number][] } = { machines: [], belts: [], routes: [] };
  for (const { tile, id, value } of change.machines) {
    const machines = world.tiles.get(tile)?.interior?.machines;
    if (!machines) continue;
    inverse.machines.push({ tile, id, value: machines.get(id) ?? null });
    if (value) machines.set(id, value);
    else machines.delete(id);
  }
  for (const { tile, key, value } of change.belts) {
    const belts = world.tiles.get(tile)?.interior?.belts;
    if (!belts) continue;
    inverse.belts.push({ tile, key, value: belts.get(key) ?? null });
    if (value) belts.set(key, value);
    else belts.delete(key);
  }
  for (const { id, value } of change.routes) {
    inverse.routes.push({ id, value: world.routes.get(id) ?? null });
    if (value) world.routes.set(id, value);
    else world.routes.delete(id);
  }
  world.layoutVersion++;
  // Undo must restore in the opposite order, in case a patch touches the same thing twice.
  return { machines: inverse.machines.reverse(), belts: inverse.belts.reverse(), routes: inverse.routes.reverse() };
}

export type Outcome = { readonly ok: true; readonly label: string } | { readonly ok: false; readonly reason: string };

interface Entry {
  readonly label: string;
  readonly redo: Patch;
  readonly undo: Patch;
}

/** The command log: runs commands and keeps what is needed to undo and redo them. */
export class History {
  private readonly done: Entry[] = [];
  private readonly undone: Entry[] = [];

  constructor(private readonly limit = 100) {}

  execute(world: World, command: Command): Outcome {
    const result = plan(world, command);
    if (!result.ok) return result;
    world.nextId += result.ids;
    const undo = applyPatch(world, result.patch);
    this.done.push({ label: result.label, redo: result.patch, undo });
    if (this.done.length > this.limit) this.done.shift();
    this.undone.length = 0;
    return { ok: true, label: result.label };
  }

  /** Undoes the last command; returns its label, or nothing if there is nothing to undo. */
  undo(world: World): string | undefined {
    const entry = this.done.pop();
    if (!entry) return undefined;
    applyPatch(world, entry.undo);
    this.undone.push(entry);
    return entry.label;
  }

  redo(world: World): string | undefined {
    const entry = this.undone.pop();
    if (!entry) return undefined;
    applyPatch(world, entry.redo);
    this.done.push(entry);
    return entry.label;
  }

  get undoLabel(): string | undefined {
    return this.done[this.done.length - 1]?.label;
  }

  get redoLabel(): string | undefined {
    return this.undone[this.undone.length - 1]?.label;
  }

  clear(): void {
    this.done.length = 0;
    this.undone.length = 0;
  }
}

const COMMAND_TYPES: readonly CommandType[] = ['placeMachine', 'placeBelt', 'remove', 'rotate', 'moveMachine', 'createRoute', 'removeRoute'];

/** Reads a command from untrusted JSON (scenario files). References are checked when it runs. */
export function parseCommand(raw: unknown, path: string): Command {
  const v = record(raw, path);
  const type = oneOf(v.type, COMMAND_TYPES, `${path}.type`);
  switch (type) {
    case 'placeMachine': {
      const rotation = v.rotation ?? 0;
      if (!isRotation(rotation)) fail(`${path}.rotation`, 'debe ser 0, 1, 2 o 3');
      return { type, tile: text(v.tile, `${path}.tile`), machine: text(v.machine, `${path}.machine`), cell: cell(v.cell, `${path}.cell`), rotation };
    }
    case 'placeBelt':
      return {
        type,
        tile: text(v.tile, `${path}.tile`),
        path: list(v.path, `${path}.path`).map((c, i) => cell(c, `${path}.path[${i}]`)),
        ...(v.exit === undefined ? {} : { exit: side(v.exit, `${path}.exit`) }),
      };
    case 'remove':
    case 'rotate':
      return { type, tile: text(v.tile, `${path}.tile`), cell: cell(v.cell, `${path}.cell`) };
    case 'moveMachine': {
      const rotation = v.rotation ?? 0;
      if (!isRotation(rotation)) fail(`${path}.rotation`, 'debe ser 0, 1, 2 o 3');
      return { type, tile: text(v.tile, `${path}.tile`), cell: cell(v.cell, `${path}.cell`), to: cell(v.to, `${path}.to`), rotation };
    }
    case 'createRoute':
      return {
        type,
        from: text(v.from, `${path}.from`),
        to: text(v.to, `${path}.to`),
        vehicle: text(v.vehicle, `${path}.vehicle`),
        ...(v.fromPort === undefined ? {} : { fromPort: text(v.fromPort, `${path}.fromPort`) }),
        ...(v.toPort === undefined ? {} : { toPort: text(v.toPort, `${path}.toPort`) }),
      };
    case 'removeRoute':
      return { type, route: text(v.route, `${path}.route`) };
  }
}
